import { alpha, createTheme, type Theme } from '@mui/material/styles';
import type { TranslationStatus } from '@greco-gls/shared';

/**
 * GrECo Spectrum, applied to a screen people stare at all day: a dense grid of text in twenty
 * languages.
 *
 * The rules of the design language, and where each one lives here:
 *  - Blue is the chrome, everything else is a mark. Surfaces are white on a pale blue ground,
 *    actions are GrECo Blue; colour appears as dots, stripes and chart fills, never as a wash.
 *  - Orange means "somebody has to act" and nothing else — it is `warning`, so a draft waiting for
 *    review, the attention list and the bell share it.
 *  - Words carry status, colour only confirms it.
 *  - Lexend only: light (300) for page titles and big numbers, semibold (600) for emphasis.
 *
 * Status and coverage colours are palette entries, so a chip, a cell stripe and a progress bar can
 * never disagree about what "machine" looks like, and the dark theme recolours all of them at once.
 * Every coverage step names its text colour, at least 4.5:1 against its fill (WCAG AA).
 *
 * Layout, type scale, spacing, radii and motion are the same in both modes; only tokens change.
 */

type StatusPalette = Record<TranslationStatus, string>;

export interface CoverageSwatch {
  fill: string;
  ink: string;
}

/** The tokens a component needs by name — the ones MUI's palette has no slot for. */
export interface SpectrumTokens {
  /** Page background. */
  ground: string;
  /** Selected and hovered rows, the active navigation entry, quiet fills. */
  tint: string;
  /** Borders and dividers. */
  hairline: string;
  /** Lines between table rows: one step lighter than a border. */
  rowLine: string;
  /** "Something for you to do": the dot, the text on a tint, and the tint itself. */
  act: { dot: string; ink: string; tint: string };
  /** A finished, good outcome ("Reviewed", "Published"). */
  done: { ink: string; tint: string };
  /** Blue that reads as a mark on the current ground: links, numbers, bars. */
  mark: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    status: StatusPalette;
    /** Five steps, least to most translated — `COVERAGE_STEPS` in the dashboard names them. */
    coverage: readonly CoverageSwatch[];
    /** The source column of the coverage matrix, which is not on the scale. */
    coverageSource: string;
    spectrum: SpectrumTokens;
  }
  interface PaletteOptions {
    status?: StatusPalette;
    coverage?: readonly CoverageSwatch[];
    coverageSource?: string;
    spectrum?: SpectrumTokens;
  }
}

export const FONT_FAMILY =
  '"Lexend Variable", Lexend, "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';

/** "Settle, never fly": short, decelerating, an 8 px rise at most. */
export const MOTION = {
  micro: 120,
  standard: 200,
  expressive: 320,
  settle: 'cubic-bezier(.2, 0, 0, 1)',
  leave: 'cubic-bezier(.4, 0, 1, 1)',
  move: 'cubic-bezier(.4, 0, .2, 1)',
} as const;

export type ColorMode = 'light' | 'dark';

interface Tokens {
  mode: ColorMode;
  primary: { main: string; dark: string; light: string; contrastText: string };
  /** The colour a contained button is filled with — on dark, a step brighter than `primary`. */
  buttonFill: string;
  text: { primary: string; secondary: string; disabled: string };
  surface: string;
  spectrum: SpectrumTokens;
  status: StatusPalette;
  coverage: readonly CoverageSwatch[];
  coverageSource: string;
  error: string;
  info: string;
  tooltip: string;
  shadow: string;
}

const LIGHT: Tokens = {
  mode: 'light',
  primary: { main: '#235592', dark: '#24497C', light: '#4F7DB5', contrastText: '#FFFFFF' },
  buttonFill: '#235592',
  text: { primary: '#1F2F45', secondary: '#5B718C', disabled: '#8A9BB0' },
  surface: '#FFFFFF',
  spectrum: {
    ground: '#F7F9FC',
    tint: '#EEF3F9',
    hairline: '#DBE4EF',
    rowLine: '#EEF3F9',
    act: { dot: '#F39200', ink: '#9A5A00', tint: '#FFF1DE' },
    done: { ink: '#0E6B47', tint: '#E6F6EF' },
    mark: '#235592',
  },
  status: { empty: '#5B718C', machine: '#7E4192', draft: '#A86200', reviewed: '#0E7A50' },
  coverage: [
    { fill: '#EEF3F9', ink: '#1F2F45' },
    { fill: '#D3E0EF', ink: '#1F2F45' },
    { fill: '#8FAED3', ink: '#1F2F45' },
    { fill: '#3F6CA6', ink: '#FFFFFF' },
    { fill: '#235592', ink: '#FFFFFF' },
  ],
  coverageSource: '#F7F9FC',
  error: '#C62828',
  info: '#7E4192',
  tooltip: '#1F2F45',
  shadow: 'rgba(31, 47, 69, 0.12)',
};

/** The ground becomes the brand's deepened blue; nothing is pure black, nothing pure white. */
const DARK: Tokens = {
  mode: 'dark',
  primary: { main: '#9CC4F0', dark: '#C4DCF7', light: '#C4DCF7', contrastText: '#0B1C33' },
  buttonFill: '#2F6BB3',
  text: { primary: '#EAF0F8', secondary: '#9FB3CC', disabled: '#6F86A3' },
  surface: '#132A4D',
  spectrum: {
    ground: '#0B1C33',
    tint: '#1B3660',
    hairline: '#26406B',
    rowLine: '#1E3558',
    act: { dot: '#F39200', ink: '#F5B560', tint: '#3A2A14' },
    done: { ink: '#5FD3A3', tint: '#143A2C' },
    mark: '#9CC4F0',
  },
  status: { empty: '#9FB3CC', machine: '#A77BC6', draft: '#F5B560', reviewed: '#5FD3A3' },
  coverage: [
    { fill: '#1B3660', ink: '#C9D6E6' },
    { fill: '#24477A', ink: '#EAF0F8' },
    { fill: '#2F6BB3', ink: '#FFFFFF' },
    { fill: '#6FA3DC', ink: '#0B1C33' },
    { fill: '#9CC4F0', ink: '#0B1C33' },
  ],
  coverageSource: '#0F2240',
  error: '#F28B82',
  info: '#A77BC6',
  tooltip: '#EAF0F8',
  shadow: 'rgba(0, 0, 0, 0.45)',
};

function build(t: Tokens): Theme {
  const { spectrum } = t;
  const isDark = t.mode === 'dark';

  return createTheme({
    shape: { borderRadius: 8 },
    palette: {
      mode: t.mode,
      primary: t.primary,
      // No second brand accent: orange is reserved for "act", the rest is blue.
      secondary: { main: t.text.secondary },
      background: { default: spectrum.ground, paper: t.surface },
      text: t.text,
      divider: spectrum.hairline,
      action: {
        selected: spectrum.tint,
        hover: alpha(t.primary.main, isDark ? 0.08 : 0.04),
        focus: alpha(t.primary.main, 0.12),
      },
      success: { main: isDark ? '#2FC88E' : '#17AB72', dark: spectrum.done.ink, light: spectrum.done.tint },
      warning: {
        main: spectrum.act.dot,
        dark: spectrum.act.ink,
        light: spectrum.act.tint,
        contrastText: '#1F2F45',
      },
      error: { main: t.error },
      info: { main: t.info },
      status: t.status,
      coverage: t.coverage,
      coverageSource: t.coverageSource,
      spectrum,
    },
    typography: {
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontSize: '1.5rem', fontWeight: 300, lineHeight: 1.15, letterSpacing: '-0.01em' },
      h2: { fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.3 },
      h3: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.35 },
      h4: { fontSize: '0.9375rem', fontWeight: 600 },
      h5: { fontSize: '0.875rem', fontWeight: 600 },
      h6: { fontSize: '0.875rem', fontWeight: 600 },
      body1: { fontSize: '0.875rem', lineHeight: 1.45 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.45 },
      caption: { fontSize: '0.75rem' },
      overline: { fontSize: '0.6875rem', letterSpacing: '0.08em', fontWeight: 500 },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    transitions: {
      duration: {
        shortest: MOTION.micro,
        shorter: MOTION.standard,
        short: MOTION.standard,
        standard: MOTION.standard,
        complex: MOTION.expressive,
        enteringScreen: MOTION.expressive,
        leavingScreen: MOTION.standard,
      },
      easing: {
        easeOut: MOTION.settle,
        easeIn: MOTION.leave,
        easeInOut: MOTION.move,
        sharp: MOTION.move,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '::selection': { backgroundColor: alpha(t.primary.main, 0.2) },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '1ms !important',
              animationDelay: '0ms !important',
              transitionDuration: `${MOTION.micro}ms !important`,
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: spectrum.hairline, borderRadius: 10 },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            variants: [
              {
                props: { variant: 'contained', color: 'primary' },
                style: {
                  backgroundColor: t.buttonFill,
                  color: '#FFFFFF',
                  '&:hover': { backgroundColor: isDark ? '#3A78C2' : t.primary.dark },
                },
              },
            ],
          },
          sizeMedium: { minHeight: 36, padding: '0 14px' },
          sizeSmall: { minHeight: 30, padding: '0 10px', fontSize: '0.8125rem' },
          sizeLarge: { minHeight: 46, padding: '0 18px', fontSize: '0.9375rem' },
          outlined: {
            borderColor: spectrum.hairline,
            backgroundColor: t.surface,
            '&:hover': { borderColor: alpha(t.primary.main, 0.5), backgroundColor: spectrum.tint },
          },
          text: { '&:hover': { backgroundColor: spectrum.tint } },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: 8, '&:hover': { backgroundColor: spectrum.tint } },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderColor: spectrum.hairline,
            color: t.text.secondary,
            '&.Mui-selected': {
              backgroundColor: spectrum.tint,
              color: t.primary.main,
              '&:hover': { backgroundColor: spectrum.tint },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: t.surface,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: spectrum.hairline },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(t.primary.main, 0.45) },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1.5 },
          },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { color: t.text.secondary } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      // An Autocomplete hands its own size to the TextField it renders, overriding the one above.
      MuiAutocomplete: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          paper: { border: `1px solid ${spectrum.hairline}`, boxShadow: `0 8px 24px ${t.shadow}` },
        },
      },
      MuiChip: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { borderRadius: 999, fontWeight: 500 },
          filled: { backgroundColor: spectrum.tint, color: t.primary.main },
          outlined: { borderColor: spectrum.hairline },
          colorPrimary: { '&.MuiChip-filled': { backgroundColor: t.buttonFill, color: '#FFFFFF' } },
          colorWarning: {
            '&.MuiChip-filled': { backgroundColor: spectrum.act.tint, color: spectrum.act.ink },
          },
          colorSuccess: {
            '&.MuiChip-filled': { backgroundColor: spectrum.done.tint, color: spectrum.done.ink },
          },
        },
      },
      MuiTable: { defaultProps: { size: 'small' } },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: spectrum.ground,
              color: t.text.secondary,
              // Not upper-cased: column heads here are often locale codes, and `sr-Latn-RS` is
              // only correct in its own case.
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: spectrum.rowLine, fontVariantNumeric: 'tabular-nums' },
          sizeSmall: { padding: '9px 12px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': { backgroundColor: alpha(t.primary.main, isDark ? 0.06 : 0.03) },
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: spectrum.tint },
            '&:last-child td': { borderBottom: 0 },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: { root: { '&.Mui-active': { color: t.primary.main } } },
      },
      MuiTablePagination: {
        styleOverrides: { root: { borderTop: `1px solid ${spectrum.rowLine}` } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&.Mui-selected': { backgroundColor: spectrum.tint },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${spectrum.hairline}`,
            borderRadius: 10,
            boxShadow: `0 8px 24px ${t.shadow}`,
          },
        },
      },
      MuiPopover: { styleOverrides: { paper: { borderRadius: 10 } } },
      MuiMenuItem: { styleOverrides: { root: { fontSize: '0.875rem', minHeight: 36 } } },
      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 400 },
        styleOverrides: {
          tooltip: {
            backgroundColor: t.tooltip,
            color: isDark ? '#0B1C33' : '#FFFFFF',
            fontSize: 12,
            fontWeight: 400,
            borderRadius: 6,
            padding: '6px 10px',
          },
          arrow: { color: t.tooltip },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: isDark ? `1px solid ${spectrum.hairline}` : undefined,
            boxShadow: `0 24px 48px ${t.shadow}`,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: alpha('#0B1C33', 0.4) } },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: 18, fontWeight: 500, padding: '20px 24px 8px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '12px 24px 20px', gap: 4 } } },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderColor: spectrum.hairline, boxShadow: `0 0 32px ${t.shadow}` },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { height: 2, borderRadius: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 40,
            color: t.text.secondary,
            '&.Mui-selected': { color: t.primary.main },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            alignItems: 'center',
            variants: [
              {
                props: { variant: 'standard', severity: 'info' },
                style: { backgroundColor: spectrum.tint, color: t.text.primary },
              },
              {
                props: { variant: 'standard', severity: 'warning' },
                style: {
                  backgroundColor: spectrum.act.tint,
                  color: isDark ? spectrum.act.ink : '#5C3600',
                },
              },
              {
                props: { variant: 'standard', severity: 'success' },
                style: {
                  backgroundColor: spectrum.done.tint,
                  color: isDark ? spectrum.done.ink : '#0B5236',
                },
              },
            ],
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 999, backgroundColor: spectrum.tint },
          bar: { borderRadius: 999 },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { '&.Mui-checked + .MuiSwitch-track': { opacity: 1 } },
          track: { backgroundColor: isDark ? '#4A6488' : '#B7C6D8', opacity: 1 },
        },
      },
      MuiBadge: {
        styleOverrides: { badge: { fontWeight: 600, fontSize: 11 } },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: spectrum.hairline } } },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${spectrum.hairline}`,
            borderRadius: 10,
            '&::before': { display: 'none' },
          },
        },
      },
      MuiLink: { styleOverrides: { root: { color: spectrum.mark, fontWeight: 500 } } },
      MuiSnackbarContent: { styleOverrides: { root: { borderRadius: 8 } } },
    },
  });
}

export const lightTheme = build(LIGHT);
export const darkTheme = build(DARK);

export const themes: Record<ColorMode, Theme> = { light: lightTheme, dark: darkTheme };

/** The light theme: what tests render with and what the app shows until a mode is resolved. */
export const theme = lightTheme;
